<template>
   <div data-cy="boolean-choice-v1">
      <SelectChoiceGroup :choices="choices" group="boolean-choice" @choice-updated="updateChoices" />
      <InvalidInputsList :errorList="errorList" :class="{ hidden: !showBlockErrors }" />
   </div>
</template>

<script setup lang="ts">
import { useI18n } from "@/vue-i18n/vue-i18n";
import type { AnswerResp } from "@/data/api-layer/answersApi";
import type { FlowselBlock } from "@/components/flowsel/flowsel.type";

export interface BooleanChoiceAnswerData {
   boolean: boolean | null;
}

export interface BooleanChoiceBlock extends FlowselBlock {
   data: {
      true: string;
      false: string;
      null: string;
   };
}

const { t: $t } = useI18n();

const emit = defineEmits(["update-valid", "update-answer"]);

// props
// -----------------------------------------
interface Props {
   block: BooleanChoiceBlock;
   userBlockAnswer: AnswerResp | null;
   showBlockErrors: boolean;
}
const props = defineProps<Props>();

// component data
// -----------------------------------------
const state = reactive({
   currentAnswer: props.userBlockAnswer?.data,
});

// computed properties
// -----------------------------------------
const choices = computed(() => {
   const theChoices = [];
   // In this instance the block data is an object, we need an array of objects
   for (const [key, value] of Object.entries(props.block.data)) {
      // (items should be in a particular order: true, false, null)
      let insertIndex = 0;
      if (key === "false") insertIndex = 1;
      if (key === "null") insertIndex = 2;
      theChoices.splice(insertIndex, 0, { id: key, label: value });
   }

   return theChoices.map((choice) => {
      const answer = props.userBlockAnswer?.data?.boolean;

      let val;
      if (answer === true) val = "true";
      else if (answer === false) val = "false";
      else if (answer === null) val = "null";

      return {
         ...choice,
         isSelected: val === choice.id ? true : false,
      };
   });
});

// validation - computed
// -----------------------------------------
const isAnswerValid = computed(() => {
   // There is no validation for this component except for (possibly) required
   if (props.block.isRequired && state.currentAnswer?.boolean === undefined) return false;
   else return true;
});

const errorList = computed(() => {
   if (!isAnswerValid.value)
      return [
         {
            inputId: props.block.id,
            validator: "required",
            message: $t("validation.choice.required"),
         },
      ];
   return [];
});

// component methods
// -----------------------------------------
const updateChoices = (userChoice) => {
   let val = null;
   if (userChoice.id === "true") val = true;
   else if (userChoice.id === "false") val = false;

   state.currentAnswer = { boolean: val };
   processInputs();
};

function processInputs() {
   if (!isAnswerValid.value) {
      // an input is invalid or has errors
      emit("update-valid", false);
   } else {
      // all inputs are valid. This may be because there is a valid answer, or no answer
      // when this question is not required. For no answer, a different data format is required by the BE.
      const answerToSubmit = state.currentAnswer?.boolean === undefined ? null : state.currentAnswer;
      emit("update-valid", true);
      emit("update-answer", answerToSubmit);
   }
}

// This is needed on initialise because validity is being evaluated & answers updated on input
// so if you go back or already have an answer, it would not be automatically evaluated
processInputs();
</script>
